import {Injectable, inject} from '@angular/core';
import {AuthService} from './auth.service';
import {OrganizationApiService, OrganizationView} from './api/routes/organization-api.service';
import {UserInfo} from '../model/user-info';
import {EnterpriseRole} from '../enums/enterprise-role';
import {Logger} from '../utils/logger';

/**
 * The organisation the signed-in account works for, as its staff see it:
 * profile, policy, teams, sign-in provider and members.
 *
 * All of it lives on the OWNER's user document, and the Enterprise screens
 * read the signed-in account's own document. For the owner that is the same
 * thing. For an Admin or Manager who is not the owner it was their own, empty
 * one: no teams, a blank profile and policy, no single sign-on -- and saving
 * the profile wrote that blankness over the organisation's.
 *
 * The owner keeps reading its own document, live. Admins and Managers get the
 * same fields from GET /organization, fetched when they sign in and again
 * after anything here changes (`refresh`).
 */
@Injectable({providedIn: 'root'})
export class OrganizationService {
  private auth = inject(AuthService);
  private api = inject(OrganizationApiService);

  /** The last organisation fetched for a member, and for whom. */
  private remote: OrganizationView | null = null;
  private remoteFor = '';
  private loading: Promise<OrganizationView | null> | null = null;

  constructor() {
    this.auth.userInfoSubject.subscribe(info => {
      if (this.isStaffMember(info) && this.remoteFor !== info.uid) {
        this.refresh();
      } else if (!info) {
        this.remote = null;
        this.remoteFor = '';
      }
    });
  }

  /** An Admin or Manager of someone else's organisation. */
  private isStaffMember(info: UserInfo | null): boolean {
    return !!info && !!info.enterpriseUid
      && (info.enterpriseRole === EnterpriseRole.ADMIN || info.enterpriseRole === EnterpriseRole.MANAGER);
  }

  /**
   * The organisation, or null while a member's copy is loading (or when the
   * account is an ordinary member, who has no view of it).
   */
  get current(): OrganizationView | null {
    const info = this.auth.currentUserInfo;
    if (!info) {
      return null;
    }
    if (!info.enterpriseUid) {
      return OrganizationService.fromOwner(info);
    }
    return this.remoteFor === info.uid ? this.remote : null;
  }

  /** The organisation's id: its owner's uid ('' until known). */
  get orgUid(): string {
    return this.current?.orgUid ?? '';
  }

  /**
   * Whether the account is an Admin or Manager of the organisation that owns,
   * or employs, `creatorUid`: like isOrgStaffFor in the Firestore rules, both
   * roles see and manage every project of their organisation. The owner's
   * own projects are known at once; a member's need the organisation's member
   * list, so this is false until ready() has it.
   */
  isStaffFor(creatorUid: string): boolean {
    const info = this.auth.currentUserInfo;
    if (!creatorUid || !this.isStaffMember(info) || creatorUid === info.uid) {
      return false;
    }
    if (creatorUid === info.enterpriseUid) {
      return true;
    }
    const org = this.current;
    return !!org && org.orgUid === info.enterpriseUid && (org.memberUids || []).includes(creatorUid);
  }

  /** Fetches a member's copy again; the owner's is always current. */
  refresh(): Promise<OrganizationView | null> {
    const info = this.auth.currentUserInfo;
    if (!this.isStaffMember(info)) {
      return Promise.resolve(this.current);
    }
    if (this.loading) {
      return this.loading;
    }
    const uid = info.uid;
    this.loading = this.api.getOrganization().then(view => {
      this.remote = view;
      this.remoteFor = uid;
      return view;
    }).catch(err => {
      Logger.error('Failed to load the organisation', err);
      return this.remoteFor === uid ? this.remote : null;
    }).finally(() => {
      this.loading = null;
    });
    return this.loading;
  }

  /** The current view, fetching a member's first if it has not arrived yet. */
  async ready(): Promise<OrganizationView | null> {
    return this.current ?? await this.refresh();
  }

  private static fromOwner(info: UserInfo): OrganizationView {
    const profile = info.enterpriseProfile ?? null;
    return {
      orgUid: info.uid,
      isOwner: true,
      role: '',
      canManage: true,
      name: profile?.name || info.company || '',
      profile,
      settings: info.enterpriseSettings ?? null,
      teams: info.enterpriseTeams ?? [],
      provider: info.enterpriseProvider ?? '',
      memberUids: info.enterpriseUsers ?? [],
    };
  }
}
